import React, { useState } from "react";
import { useForm, useField } from "react-final-form";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { Translate } from "react-thunk-i18nify";

import ServiceModal from "components/CellPhone/Form/NetworkOperatorSubscriptionServices/Modal/ServiceModal";
import WholePageSpinner from "components/Spinner/WholePageSpinner";

const ServiceTableRow = ({
  fields,
  fieldName,
  currentIndex,
  filteredSubscriptionServices,
  setFilteredSubscriptionServices,
  cellPhoneServices,
}) => {
  const [modalVisible, setModalVisibility] = useState(false);

  const form = useForm();

  const subscriptionServiceField = useField(fieldName);

  const {
    input: { value: subscriptionService },
    meta: { invalid: isUpdatedSubscriptionServiceInvalid },
  } = subscriptionServiceField;

  const {
    id,
    location,
    cell_phone_service_id: serviceID,
    location_constraint: locationConstraint,
  } = subscriptionService;

  const {
    input: { value: subscriptionServiceInitialValue },
  } = useField("subscription_service_currently_on_edit");

  const toggleModalVisibility = () => {
    setModalVisibility((previousState) => !previousState);
  };

  const updateSubscriptionService = () => {
    if (isUpdatedSubscriptionServiceInvalid) {
      return;
    }

    fields.update(currentIndex, subscriptionService);

    setFilteredSubscriptionServices((prev) => {
      const filteredOptions = prev.filter(
        ({ id: currentServiceID }) => currentServiceID !== serviceID
      );

      return filteredOptions;
    });

    toggleModalVisibility();
  };

  const resetSubscriptionServiceUpdate = () => {
    toggleModalVisibility();

    setFilteredSubscriptionServices((prev) => {
      const currentServiceToRemoveFromTheFilterOptions = cellPhoneServices.find(
        ({ id: currentServiceID }) => currentServiceID === serviceID
      );

      const filterOptions = prev.filter(
        ({ id: currentServiceID }) =>
          currentServiceID !== currentServiceToRemoveFromTheFilterOptions.id
      );

      return filterOptions;
    });

    if (subscriptionServiceField.meta.dirty) {
      fields.update(currentIndex, subscriptionServiceInitialValue);
    }
  };

  const handleRemoveSubscriptionService = (index) => {
    const rowToRemoveFromNetworkOperatorSubscriptionServices =
      form.getFieldState(`network_operator_subscription_services[${index}]`);

    const {
      value: { cell_phone_service_id: removedServiceID },
    } = rowToRemoveFromNetworkOperatorSubscriptionServices;
    const addDeselectedServiceToTheFilterDropdownOptions =
      cellPhoneServices.find(
        ({ id: currentServiceID }) => currentServiceID === removedServiceID
      );

    fields.remove(index);

    setFilteredSubscriptionServices((prev) => [
      ...prev,
      addDeselectedServiceToTheFilterDropdownOptions,
    ]);

    if (id) {
      fields.push({ id, _destroy: true });
    }
  };

  const handleSubscriptionEditButtonClick = () => {
    setFilteredSubscriptionServices((prev) => {
      const currentServiceToAddInTheFilterOptions = cellPhoneServices.find(
        ({ id: currentServiceID }) => currentServiceID === serviceID
      );

      return [...prev, currentServiceToAddInTheFilterOptions];
    });

    toggleModalVisibility();

    form.change("subscription_service_currently_on_edit", subscriptionService);
  };

  if (cellPhoneServices.length === 0) return <WholePageSpinner />;

  const currentSubscriptionService = cellPhoneServices.find(
    (cellPhoneService) => cellPhoneService.id === serviceID
  );

  const { name: serviceName } = currentSubscriptionService;

  return (
    <tr className="subscription-listing-table-row">
      <td className="align-middle text-center p-3">
        <p className="mb-0">{serviceName}</p>
      </td>
      <td className="align-middle text-center p-3">
        <p className="mb-0">
          <Translate value={`cellPhone.networkServices.location.${location}`} />
        </p>
      </td>
      <td className="align-middle text-center p-3">
        <p className="mb-0">
          <Translate
            value={`cellPhone.networkServices.locationConstraint.${locationConstraint}`}
          />
        </p>
      </td>
      <td className="d-flex justify-content-evenly align-middle p-3">
        <ServiceModal
          fieldName={fieldName}
          title={<Translate value="common.editService" />}
          buttonTitle={<Translate value="common.updateService" />}
          modalVisible={modalVisible}
          onSubmit={updateSubscriptionService}
          onCancel={resetSubscriptionServiceUpdate}
          subscriptionServiceField={subscriptionServiceField}
          filteredSubscriptionServices={filteredSubscriptionServices}
          setFilteredSubscriptionServices={setFilteredSubscriptionServices}
        />

        <button
          className="border border-success rounded p-2 text-success d-flex align-self-center bg-transparent"
          type="button"
          onClick={handleSubscriptionEditButtonClick}
        >
          <FontAwesomeIcon icon={faPencilAlt} />
        </button>
        <button
          className="border border-danger rounded p-2 text-danger d-flex align-self-center bg-transparent"
          type="button"
          onClick={() => handleRemoveSubscriptionService(currentIndex)}
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
      </td>
    </tr>
  );
};

ServiceTableRow.defaultProps = {};

ServiceTableRow.propTypes = {
  fields: PropTypes.shape({
    value: PropTypes.arrayOf(
      PropTypes.shape({
        cell_phone_service_id: PropTypes.number,
        location: PropTypes.string,
        location_constraint: PropTypes.string,
      })
    ),
    push: PropTypes.func,
    remove: PropTypes.func,
    update: PropTypes.func,
  }).isRequired,
  fieldName: PropTypes.string.isRequired,
  currentIndex: PropTypes.number.isRequired,
  filteredSubscriptionServices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      description: PropTypes.string,
      is_default: PropTypes.bool,
    })
  ).isRequired,
  setFilteredSubscriptionServices: PropTypes.func.isRequired,
  cellPhoneServices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      description: PropTypes.string,
      is_default: PropTypes.bool,
    })
  ).isRequired,
};

export default ServiceTableRow;
